import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { simulationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { PiggyBank, TrendingUp, AlertTriangle, Lightbulb, Target, Calculator, ArrowRight } from 'lucide-react';

// Données comparatif retraite AE vs EI
const COMPARATIF_DATA = {
  scenarios: [
    { duree: 10, ca_mensuel: 2000, ae_pension: { min: 150, max: 250 }, ei_pension: { min: 500, max: 650 } },
    { duree: 15, ca_mensuel: 2000, ae_pension: { min: 250, max: 350 }, ei_pension: { min: 750, max: 900 } },
    { duree: 20, ca_mensuel: 2000, ae_pension: { min: 350, max: 450 }, ei_pension: { min: 1000, max: 1200 } },
    { duree: 25, ca_mensuel: 2000, ae_pension: { min: 400, max: 600 }, ei_pension: { min: 1200, max: 1500 } },
    { duree: 30, ca_mensuel: 2000, ae_pension: { min: 500, max: 700 }, ei_pension: { min: 1400, max: 1700 } },
  ]
};

const EpargneRetraitePage = () => {
  const [simulating, setSimulating] = useState(false);
  const [formData, setFormData] = useState({
    chiffre_affaires_mensuel: '',
    age: '',
    type_activite: 'BIC_SERVICE',
    duree_cotisation_prevue: '25'
  });
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('simulateur');

  const handleSimulate = async (e) => {
    e.preventDefault();
    setSimulating(true);
    try {
      const response = await simulationsAPI.epargneRetraite({
        chiffre_affaires_mensuel: parseFloat(formData.chiffre_affaires_mensuel),
        age: parseInt(formData.age),
        type_activite: formData.type_activite
      });
      
      // Calcul dynamique des pensions basé sur le CA réel
      const ca_mensuel = parseFloat(formData.chiffre_affaires_mensuel);
      const duree = parseInt(formData.duree_cotisation_prevue);
      const ca_reference = 2000; // CA de référence dans les scénarios
      
      // Facteur proportionnel au CA
      const facteur_ca = ca_mensuel / ca_reference;
      
      // Trouver le scénario de base pour la durée
      const scenario = COMPARATIF_DATA.scenarios.find(s => s.duree === duree) 
        || COMPARATIF_DATA.scenarios[3]; // Default 25 ans
      
      // Calculer les pensions proportionnellement au CA
      const ae_pension_min = Math.round(scenario.ae_pension.min * facteur_ca);
      const ae_pension_max = Math.round(scenario.ae_pension.max * facteur_ca);
      const ei_pension_min = Math.round(scenario.ei_pension.min * facteur_ca);
      const ei_pension_max = Math.round(scenario.ei_pension.max * facteur_ca);
      
      setResult({
        ...response.data,
        comparatif: {
          ae_pension: { min: ae_pension_min, max: ae_pension_max },
          ei_pension: { min: ei_pension_min, max: ei_pension_max },
          difference_mensuelle: ei_pension_min - ae_pension_max,
          difference_annuelle: (ei_pension_min - ae_pension_max) * 12,
          difference_20_ans: (ei_pension_min - ae_pension_max) * 12 * 20
        }
      });
    } catch (error) {
      toast.error('Erreur lors de la simulation');
    } finally {
      setSimulating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  return (
    <div className="space-y-6" data-testid="epargne-retraite-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Épargne retraite</h1>
        <p className="text-muted-foreground">Préparez votre avenir avec des recommandations personnalisées</p>
      </div>

      {/* Warning Card */}
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="h-8 w-8 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-bold font-['Manrope'] text-red-900 mb-2">
                Retraite auto-entrepreneur : attention danger !
              </h3>
              <p className="text-red-800 mb-2">
                <strong>Exemple concret :</strong> Un AE qui cotise pendant <strong>25 ans</strong> avec un CA moyen de <strong>2 000 €/mois</strong> 
                touchera environ <strong>400 à 600 €/mois</strong> de retraite.
              </p>
              <p className="text-red-800">
                En entreprise individuelle classique avec les mêmes revenus, ce serait <strong>1 200 à 1 500 €/mois</strong>. 
                Soit une différence de <strong>600 à 900 €/mois</strong> !
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="simulateur">Simulateur personnalisé</TabsTrigger>
          <TabsTrigger value="comparatif">Comparatif AE vs EI</TabsTrigger>
        </TabsList>

        {/* Simulateur Tab */}
        <TabsContent value="simulateur">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Simulateur */}
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Calculateur d'épargne
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSimulate} className="space-y-6">
                  <div className="space-y-2">
                    <Label>Type d'activité</Label>
                    <Select
                      value={formData.type_activite}
                      onValueChange={(value) => setFormData({ ...formData, type_activite: value })}
                    >
                      <SelectTrigger data-testid="epargne-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BIC_VENTE">Vente de marchandises (BIC)</SelectItem>
                        <SelectItem value="BIC_SERVICE">Prestation de services (BIC)</SelectItem>
                        <SelectItem value="BNC">Libéral régime général (BNC)</SelectItem>
                        <SelectItem value="BNC_CIPAV">Libéral CIPAV (BNC)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Votre âge</Label>
                    <Input
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      placeholder="Ex: 35"
                      min="18"
                      max="67"
                      required
                      data-testid="epargne-age"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>CA mensuel moyen (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.chiffre_affaires_mensuel}
                      onChange={(e) => setFormData({ ...formData, chiffre_affaires_mensuel: e.target.value })}
                      placeholder="Ex: 3000"
                      required
                      data-testid="epargne-ca"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Durée de cotisation prévue</Label>
                    <Select
                      value={formData.duree_cotisation_prevue}
                      onValueChange={(value) => setFormData({ ...formData, duree_cotisation_prevue: value })}
                    >
                      <SelectTrigger data-testid="epargne-duree">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 ans</SelectItem>
                        <SelectItem value="15">15 ans</SelectItem>
                        <SelectItem value="20">20 ans</SelectItem>
                        <SelectItem value="25">25 ans</SelectItem>
                        <SelectItem value="30">30 ans</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={simulating}
                    data-testid="epargne-simulate"
                  >
                    {simulating ? 'Calcul...' : 'Calculer mon épargne recommandée'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Résultat */}
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Vos recommandations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {result ? (
                  <div className="space-y-6">
                    {/* Revenus */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">CA annuel</p>
                        <p className="text-xl font-bold font-['Manrope']">{formatCurrency(result.chiffre_affaires_annuel)}</p>
                      </div>
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Revenu net</p>
                        <p className="text-xl font-bold font-['Manrope'] text-emerald-600">{formatCurrency(result.revenu_net_annuel)}</p>
                      </div>
                    </div>

                    {/* Comparatif pension */}
                    {result.comparatif && (
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <h4 className="font-medium text-red-900 mb-3">Pension estimée après {formData.duree_cotisation_prevue} ans</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-red-700">En auto-entrepreneur</p>
                            <p className="text-lg font-bold text-red-600">
                              {result.comparatif.ae_pension.min} - {result.comparatif.ae_pension.max} €/mois
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-emerald-700">En entreprise individuelle</p>
                            <p className="text-lg font-bold text-emerald-600">
                              {result.comparatif.ei_pension.min} - {result.comparatif.ei_pension.max} €/mois
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-red-200">
                          <p className="text-sm text-red-800">
                            Manque à gagner estimé : <strong>{formatCurrency(result.comparatif.difference_20_ans)}</strong> sur 20 ans de retraite
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Trimestres */}
                    <div className="p-4 border rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-muted-foreground">Trimestres retraite validés/an</span>
                        <span className="text-2xl font-bold font-['Manrope']">{result.trimestres_retraite_estimes}/4</span>
                      </div>
                      <Progress value={(result.trimestres_retraite_estimes / 4) * 100} className="h-3" />
                      <p className="text-xs text-amber-600 mt-2">{result.alerte_retraite}</p>
                    </div>

                    {/* Recommandation épargne */}
                    <div className="p-6 bg-primary/5 rounded-xl border border-primary/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="h-5 w-5 text-primary" />
                        <span className="font-medium">Épargne recommandée</span>
                      </div>
                      <p className="text-3xl font-bold font-['Manrope'] text-primary mb-2">
                        {formatCurrency(result.epargne_mensuelle_recommandee)}/mois
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Soit {result.taux_epargne_recommande}% de votre revenu net
                      </p>
                    </div>

                    {/* Projection */}
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-primary" />
                        <span className="font-medium">Projection à 65 ans</span>
                      </div>
                      <p className="text-2xl font-bold font-['Manrope']">
                        {formatCurrency(result.capital_projete_65_ans)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Capital projeté avec un rendement moyen de 3%/an
                      </p>
                    </div>

                    {/* Conseil */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        💡 {result.conseil}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <PiggyBank className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Lancez une simulation pour voir vos recommandations</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Comparatif Tab */}
        <TabsContent value="comparatif">
          <Card>
            <CardHeader>
              <CardTitle className="font-['Manrope']">
                Comparatif retraite : Auto-entrepreneur vs Entreprise individuelle
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Base : CA mensuel de 2 000 € (revenus équivalents)
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Durée de cotisation</th>
                      <th className="text-center py-3 px-4 bg-red-50 text-red-900">Pension AE</th>
                      <th className="text-center py-3 px-4 bg-emerald-50 text-emerald-900">Pension EI</th>
                      <th className="text-center py-3 px-4">Différence</th>
                      <th className="text-center py-3 px-4">Perte sur 20 ans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARATIF_DATA.scenarios.map((scenario) => {
                      const diff = scenario.ei_pension.min - scenario.ae_pension.max;
                      const perte20ans = diff * 12 * 20;
                      return (
                        <tr key={scenario.duree} className="border-b hover:bg-muted/30">
                          <td className="py-4 px-4 font-medium">{scenario.duree} ans</td>
                          <td className="py-4 px-4 text-center bg-red-50/50">
                            <span className="font-bold text-red-600">
                              {scenario.ae_pension.min} - {scenario.ae_pension.max} €
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center bg-emerald-50/50">
                            <span className="font-bold text-emerald-600">
                              {scenario.ei_pension.min} - {scenario.ei_pension.max} €
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="text-red-600 font-medium">-{diff} €/mois</span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="text-red-600 font-bold">{formatCurrency(perte20ans)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Explication */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <Card className="border-red-200">
              <CardHeader className="bg-red-50">
                <CardTitle className="font-['Manrope'] text-red-900 text-lg">
                  Pourquoi la retraite AE est si faible ?
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                    <span className="text-sm">Cotisations proportionnelles au CA (non au revenu)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                    <span className="text-sm">Taux de cotisation retraite très bas (~0.5% à 2.2%)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                    <span className="text-sm">Pas de cotisation retraite complémentaire obligatoire</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                    <span className="text-sm">Points retraite acquis très limités</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-primary">
              <CardHeader className="bg-primary/5">
                <CardTitle className="font-['Manrope'] text-lg">
                  Comment compenser ?
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                    <span className="text-sm"><strong>PER</strong> : Déductible des impôts, bloqué jusqu'à la retraite</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                    <span className="text-sm"><strong>Assurance-vie</strong> : Plus souple, fiscalité avantageuse après 8 ans</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                    <span className="text-sm"><strong>Immobilier</strong> : Revenus locatifs à la retraite</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                    <span className="text-sm"><strong>Épargner</strong> : 10-20% de vos revenus selon votre âge</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Solutions d'épargne */}
      <Card>
        <CardHeader>
          <CardTitle className="font-['Manrope']">Solutions d'épargne retraite</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 border rounded-xl">
              <h3 className="font-bold font-['Manrope'] mb-2">PER (Plan Épargne Retraite)</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Déductible de votre revenu imposable. Idéal pour réduire vos impôts tout en épargnant.
              </p>
              <div className="text-sm">
                <span className="font-medium text-primary">✓ Avantage fiscal immédiat</span>
              </div>
            </div>
            <div className="p-6 border rounded-xl">
              <h3 className="font-bold font-['Manrope'] mb-2">Assurance-vie</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Plus souple que le PER. Disponible à tout moment avec une fiscalité avantageuse après 8 ans.
              </p>
              <div className="text-sm">
                <span className="font-medium text-primary">✓ Flexibilité maximale</span>
              </div>
            </div>
            <div className="p-6 border rounded-xl">
              <h3 className="font-bold font-['Manrope'] mb-2">Immobilier locatif</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Revenus complémentaires à la retraite. Possibilité de défiscalisation (Pinel, LMNP...).
              </p>
              <div className="text-sm">
                <span className="font-medium text-primary">✓ Patrimoine tangible</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EpargneRetraitePage;
